// ---------------------------------------------------------------------------
// Order Bump Renderer — Funnel-Aware Checkout Add-On
// ---------------------------------------------------------------------------

import { registerRenderer } from "../registry.js";
import type { RenderContext } from "../types.js";
import { attr, text, url } from "../escape.js";
import { sanitizeThemeTokenColor } from "../css-cascade.js";
import { resolveOrderBumpBinding } from "../../platform-native.js";

interface OrderBumpProps {
  productId: string;
  headline: string;
  description: string;
  originalPrice: string;
  bumpPrice: string;
  checkboxLabel?: string;
  highlightColor?: string;
  imageUrl?: string;
  binding?: unknown;
}

function render(props: Record<string, unknown>, ctx: RenderContext): string {
  if (!ctx.funnelStepId) return "";

  const p = props as unknown as OrderBumpProps;

  const productId = p.productId ?? "";
  const headline = p.headline ?? "Special Offer";
  const description = p.description ?? "";
  const originalPrice = p.originalPrice ?? "";
  const bumpPrice = p.bumpPrice ?? "";
  const checkboxLabel = p.checkboxLabel ?? "Yes, add this to my order!";
  const highlightColor = sanitizeThemeTokenColor(p.highlightColor ?? "")
    ?? "var(--ease-color-warning,var(--ease-color-accent,#f59e0b))";
  const binding = resolveOrderBumpBinding({
    productId,
    binding: p.binding,
  });

  const imageHtml = p.imageUrl
    ? `<img class="ease-bump-image" src="${url(p.imageUrl)}" alt="${attr(headline)}" loading="lazy">`
    : "";

  const priceHtml = originalPrice
    ? `<span class="ease-bump-original-price" style="text-decoration:line-through;opacity:0.6">${text(originalPrice)}</span> <span class="ease-bump-price">${text(bumpPrice)}</span>`
    : `<span class="ease-bump-price">${text(bumpPrice)}</span>`;
  const bindingFieldName = binding.fieldName ?? `_order_bump_${productId}`;
  const bindingValue = binding.selectedValue ?? "1";
  const noteId = `ease-bump-note-${productId.replace(/[^a-zA-Z0-9_-]/g, "") || "default"}`;
  const syncScript = `<script>(function(){var root=document.currentScript.previousElementSibling;if(!root)return;var checkbox=root.querySelector('.ease-bump-checkbox');var note=root.querySelector('.ease-bump-note');if(!checkbox||!note)return;var forms=document.querySelectorAll('[data-ease-form]');var fieldName=checkbox.getAttribute('data-field-name')||'';var fieldValue=checkbox.getAttribute('data-field-value')||'1';var offerId=checkbox.getAttribute('data-offer-id')||'';function ensureHidden(form,name,marker){var selector='input[type="hidden"][data-ease-bump-hidden="'+marker+'"]';var input=form.querySelector(selector);if(input)return input;input=document.createElement('input');input.type='hidden';input.name=name;input.setAttribute('data-ease-bump-hidden',marker);form.appendChild(input);return input;}function sync(){if(!forms.length){checkbox.disabled=true;checkbox.checked=false;root.setAttribute('data-bump-state','inactive');note.hidden=false;return;}checkbox.disabled=false;root.setAttribute('data-bump-state','active');note.hidden=true;forms.forEach(function(form){var selected=ensureHidden(form,fieldName,fieldName);selected.value=checkbox.checked?fieldValue:'';selected.disabled=!checkbox.checked;if(offerId){var offerField=ensureHidden(form,'_order_bump_offer_id','_order_bump_offer_id');offerField.value=offerId;offerField.disabled=!checkbox.checked;}});}checkbox.addEventListener('change',sync);sync();})();</script>`;

  return `<section class="ease-section ease-order-bump" aria-label="${attr(headline)}" style="border:2px solid ${attr(highlightColor)};border-radius:var(--ease-card-radius,var(--ease-radius,8px));padding:var(--ease-card-padding,16px);background:var(--ease-color-surface,var(--ease-color-bg,#fff));box-shadow:var(--ease-card-shadow,none)">
  <div class="ease-bump-content" style="display:flex;gap:16px;align-items:center">
    ${imageHtml}
    <div class="ease-bump-details">
      <h3 class="ease-bump-headline">${text(headline)}</h3>
      <p class="ease-bump-description">${text(description)}</p>
      <div class="ease-bump-pricing">${priceHtml}</div>
    </div>
  </div>
  <label class="ease-bump-label" style="display:flex;align-items:center;gap:8px;margin-top:12px;cursor:pointer;color:var(--ease-color-text,#1f2937)">
    <input type="checkbox" name="_bump_${attr(productId)}" class="ease-bump-checkbox" value="1" data-field-name="${attr(bindingFieldName)}" data-field-value="${attr(bindingValue)}"${binding.offerId ? ` data-offer-id="${attr(binding.offerId)}"` : ""}>
    <span>${text(checkboxLabel)}</span>
  </label>
  <p id="${attr(noteId)}" class="ease-bump-note" hidden>Order bump stays inactive until this funnel step renders a real form submission surface.</p>
</section>
${syncScript}`;
}

registerRenderer("order-bump", { render });
